import type { ReactNode } from 'react';
import { spokenFraction } from './spokenFraction';

// Shows fractions the way primary students learn to write them: one number
// above the other (design audit 2, 2026-10-04). Question content writes them
// as plain text ("3/4 of 24"), which is harder to read and compare at a
// glance -- the very skill being practised.
//
// Only simple a/b and mixed numbers (1 3/4) are drawn stacked. A run like
// 20/10/2026 (a date) is left alone, as is anything joined to more digits,
// another slash or a decimal (2.5/10). A full stop ending a sentence is fine.
const FRACTION =
  /(?<![\d/]|\d[.,])(?:(\d{1,2}) )?(\d{1,3})\/(\d{1,3})(?![\d/]|[.,]\d)/g;

// The drawing itself: an optional whole number, then one thing above
// another with a line between. Shared with the "several boxes" questions,
// where the top or the bottom is a box ("[a]/15", see BoxesAnswer.tsx and
// BoxMarkerText.tsx), and with the "fraction entry" questions, where every
// part is a box (FractionAnswer.tsx).
//
// An ordinary fraction passes neither `spoken` nor `roomy`, and is drawn
// exactly as it always was. A fraction with a box is not hidden from screen
// readers (the box is real), so `spoken` gives the words that join its
// parts. `roomy` gives the top and the bottom the same generous height, so
// a tall typing box above (or below) leaves the line level with the lines
// of the fractions beside it.
export function FractionStack({
  numerator,
  denominator,
  whole,
  label,
  spoken,
  roomy = false,
}: {
  numerator: ReactNode;
  denominator: ReactNode;
  // A number, or (fraction-entry questions, see FractionAnswer.tsx) the box
  // a whole number is typed into.
  whole?: ReactNode;
  // What the fraction was in the text: "3/4", "[a]/15".
  label: string;
  spoken?: { afterWhole: string; between: string } | undefined;
  roomy?: boolean;
}) {
  return (
    <>
      {whole !== undefined && <span className="mr-[0.15em]">{whole}</span>}
      {whole !== undefined && spoken && (
        <span className="sr-only">{spoken.afterWhole}</span>
      )}
      <span
        data-fraction={label}
        className="mx-[0.1em] inline-flex flex-col items-center align-middle text-[0.95em] leading-[1.1] font-semibold"
      >
        <span
          className={
            roomy ? 'flex min-h-11 items-end px-[0.15em] pb-1' : 'px-[0.15em]'
          }
        >
          {numerator}
        </span>
        {spoken && <span className="sr-only">{spoken.between}</span>}
        <span className="self-stretch border-t-[0.09em] border-current" />
        <span
          className={
            roomy ? 'flex min-h-11 items-start px-[0.15em] pt-1' : 'px-[0.15em]'
          }
        >
          {denominator}
        </span>
      </span>
    </>
  );
}

function StackedFraction({
  numerator,
  denominator,
  whole,
}: {
  numerator: string;
  denominator: string;
  whole?: string | undefined;
}) {
  return (
    <span className="whitespace-nowrap">
      <span className="sr-only">
        {spokenFraction(
          Number(numerator),
          Number(denominator),
          whole === undefined ? undefined : Number(whole),
        )}
      </span>
      <span aria-hidden="true">
        <FractionStack
          numerator={numerator}
          denominator={denominator}
          whole={whole}
          label={`${numerator}/${denominator}`}
        />
      </span>
    </span>
  );
}

// Recurring decimals (Year 8, 3 Nov 2026) are written with a combining dot
// over each repeating digit ("0.4̇", "0.2̇7̇"). The site's font draws that dot
// beside the digit, not over it, so each dotted digit is drawn with its own
// dot above, and a screen reader hears the number and then "recurring".
const RECURRING = /\d*\.\d*\ḋ(?:\d*\ḋ)?/g;

function DottedDigit({ digit }: { digit: string }) {
  return (
    <span className="relative inline-block">
      {digit}
      <span className="absolute -top-[0.45em] left-1/2 -translate-x-1/2 leading-none">
        ·
      </span>
    </span>
  );
}

function Recurring({ number }: { number: string }) {
  const shown: ReactNode[] = [];
  for (let i = 0; i < number.length; i++) {
    const ch = number.charAt(i);
    if (number.charAt(i + 1) === '̇') {
      shown.push(<DottedDigit key={i} digit={ch} />);
      i++;
    } else shown.push(ch);
  }
  return (
    <span className="whitespace-nowrap">
      <span className="sr-only">{number.replace(/̇/g, '')} recurring</span>
      <span aria-hidden="true">{shown}</span>
    </span>
  );
}

function withRecurring(text: string, at = 0): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(RECURRING)) {
    const start = match.index;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(<Recurring key={`r${String(at + start)}`} number={match[0]} />);
    last = start + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Plain text with any recurring decimals drawn (for BoxMarkerText).
export function RecurringText({ text }: { text: string }) {
  return <>{withRecurring(text)}</>;
}

export function MathText({ text }: { text: string }): ReactNode {
  if (text.includes('̇')) return <>{withRecurringAndFractions(text)}</>;
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(FRACTION)) {
    const [full, whole, numerator = '', denominator = ''] = match;
    const start = match.index;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <StackedFraction
        key={start}
        numerator={numerator}
        denominator={denominator}
        whole={whole}
      />,
    );
    last = start + full.length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// The same as MathText, with recurring decimals drawn between the fractions.
function withRecurringAndFractions(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(FRACTION)) {
    const [full, whole, numerator = '', denominator = ''] = match;
    const start = match.index;
    if (start > last)
      parts.push(...withRecurring(text.slice(last, start), last));
    parts.push(
      <StackedFraction
        key={start}
        numerator={numerator}
        denominator={denominator}
        whole={whole}
      />,
    );
    last = start + full.length;
  }
  if (last < text.length) parts.push(...withRecurring(text.slice(last), last));
  return parts;
}
